import { ClinicMark } from '@/components/ClinicMark'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { Avatar } from '@/components/Avatar'
import { Icon, type IconName } from '@/components/Icon'
import { useToast } from '@/components/Toast'
import { useSession } from '@/features/auth/SessionProvider'
import { cx } from '@/lib/cx'
import { useModals } from '@/app/modals/ModalContext'

interface Item {
  to: string
  label: string
  icon: IconName
  end?: boolean
}

/**
 * Grouped, and every route the app can reach is in here: a section that exists
 * only on the phone tab bar is, on the web, unreachable. "New patient" is not
 * one of these rows — it is an action, and it sits above them.
 */
const SECTIONS: { label?: string; items: Item[] }[] = [
  { items: [{ to: '/', label: 'Dashboard', icon: 'home', end: true }] },
  {
    label: 'Clinic',
    items: [
      { to: '/patients', label: 'Patients', icon: 'users' },
      { to: '/prescriptions', label: 'Prescriptions', icon: 'pill' },
    ],
  },
  {
    label: 'Booking',
    items: [
      { to: '/callers', label: 'Callers', icon: 'phone' },
      { to: '/appointments', label: 'Appointments', icon: 'calendar' },
      { to: '/travel', label: 'Travel', icon: 'plane' },
      { to: '/calendar', label: 'Calendar', icon: 'calendarDays' },
    ],
  },
  { label: 'Account', items: [{ to: '/settings', label: 'Settings', icon: 'settings' }] },
]

const ITEM =
  'flex min-h-touch items-center gap-3 rounded-md px-3 text-sm no-underline transition-colors duration-150 ease-standard'

export function Sidebar({ open = false }: { open?: boolean }) {
  const { user, signOut } = useSession()
  const navigate = useNavigate()
  const toast = useToast()
  const modals = useModals()

  return (
    // Off-canvas drawer below the breakpoint, a fixed column above it. Same
    // markup both times: two sets of navigation would be two sets of bugs.
    <nav
      id="main-nav"
      aria-label="Main"
      className={cx(
        'fixed inset-y-0 left-0 z-sheet flex w-sidebar max-w-[85vw] flex-col overflow-hidden border-r border-line-soft bg-surface shadow-lg transition-[transform,visibility] duration-200 ease-standard',
        'lg:static lg:h-full lg:max-w-none lg:flex-[0_0_var(--spacing-sidebar)] lg:visible lg:translate-x-0 lg:shadow-none lg:transition-none',
        open ? 'visible translate-x-0' : 'invisible -translate-x-full',
      )}
    >
      <Link
        to="/"
        className="flex h-topbar flex-none items-center gap-3 border-b border-line-soft px-4 text-fg no-underline"
      >
        <ClinicMark className="size-9" />
        <span className="flex min-w-0 flex-col">
          <span className="text-base leading-tight font-bold tracking-snug">Casemark</span>
          <span className="mt-0.5 truncate text-xs leading-tight text-fg-subtle">
            Dr. Warrier&apos;s Homoeo Clinic
          </span>
        </span>
      </Link>

      <button
        type="button"
        onClick={() => modals.open({ kind: 'patient', mode: 'create' })}
        className="mx-3 mt-4 mb-2 flex min-h-touch flex-none items-center justify-center gap-2 rounded-md bg-accent px-4 text-sm font-semibold text-on-accent transition-colors duration-150 ease-standard hover:bg-accent-hover active:bg-accent-active"
      >
        <Icon name="userPlus" size="1.125rem" />
        New patient
      </button>

      {/* overscroll-contain: a flick that reaches the end of this list stops
          there instead of handing the rest of the gesture to the page behind
          the drawer. */}
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-3 pt-2 pb-4">
        {SECTIONS.map((section, index) => (
          <div key={section.label ?? index} className="flex flex-col gap-0.5">
            {section.label && (
              <p className="px-3 pt-2 pb-1 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
                {section.label}
              </p>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                // Filled pill, accent icon, and the label goes to semibold —
                // three signals, so the current screen is never a matter of
                // telling two blues apart.
                className={({ isActive }) =>
                  cx(
                    ITEM,
                    isActive
                      ? 'bg-accent-soft font-semibold text-fg [&_svg]:text-accent'
                      : 'font-medium text-fg-muted hover:bg-hover hover:text-fg [&_svg]:text-fg-subtle hover:[&_svg]:text-fg-muted',
                  )
                }
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div className="flex flex-none flex-col gap-1 border-t border-line-soft p-3">
        {user && (
          <Link
            to="/settings"
            className="flex min-h-touch items-center gap-3 rounded-md px-3 text-fg no-underline transition-colors duration-150 ease-standard hover:bg-hover"
          >
            <Avatar name={user.full_name} size={32} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm leading-tight font-semibold">{user.full_name}</span>
              <span className="mt-0.5 text-xs leading-tight text-fg-subtle capitalize">
                {user.role.replace('_', ' ')}
              </span>
            </span>
          </Link>
        )}
        <button
          type="button"
          className={cx(ITEM, 'w-full font-medium text-fg-muted hover:bg-hover hover:text-fg [&_svg]:text-fg-subtle')}
          onClick={async () => {
            const reachedServer = await signOut()
            if (!reachedServer) {
              toast.showError('Signed out on this device, but the server could not be reached.')
            }
            navigate('/', { replace: true })
          }}
        >
          <Icon name="signOut" size="1.125rem" />
          Log out
        </button>
      </div>
    </nav>
  )
}
